import { useEffect, useState, type ReactNode } from "react";
import { api } from "../api/client.js";
import {
  fetchSecretStatuses,
  saveSecret,
  type SecretStatus,
} from "../api/integrations.js";

export function SettingsPage() {
  return (
    <div>
      <h2 style={{ margin: 0, fontSize: "1.5rem" }}>Настройки</h2>
      <p style={{ color: "var(--text-dim)", margin: "0 0 2rem" }}>
        Конфигурация агента · readonly в Phase 1C, редактирование Phase 2
      </p>

      <Section title="Аварийный СТОП">
        <KillSwitchPanel />
      </Section>

      <Section title="Автоматизация гипотез">
        <AutoKillToggle />
      </Section>

      <Section title="Целевые KPI">
        <FormRow
          label="Target net-ROAS"
          value="280%"
          hint="% выручки на 1 ₽ рекламы (после поправки на возвраты)"
        />
        <FormRow label="Min clicks (базовые гипотезы)" value="80" />
        <FormRow label="Min spent на гипотезу" value="1 500 ₽" />
        <FormRow label="Max runtime гипотезы" value="21 день" />
        <FormRow label="Min runtime гипотезы" value="7 дней" />
      </Section>

      <Section title="Лимиты бюджета">
        <FormRow label="Месячный бюджет" value="200 000 ₽/мес" />
        <FormRow
          label="Brand Shield daily cap"
          value="800 ₽/день · ~7.5%"
        />
        <FormRow
          label="Performance Core daily cap"
          value="2 500 ₽/день · ~22.5%"
        />
        <FormRow
          label="Test Factory daily cap"
          value="5 000 ₽/день · ~70%"
        />
        <FormRow label="Hard daily cap (общий)" value="8 000 ₽/день" />
      </Section>

      <Section title="Quality-of-Click guard">
        <FormRow label="Max bounce rate" value="70%" />
        <FormRow label="Min session depth" value="1.5" />
        <FormRow label="AOV-drift алерт" value="15%" />
      </Section>

      <Section title="Pre-flight validator">
        <FormRow label="Я.Директ rules" value="✓ ON" />
        <FormRow label="Brand-DNA gate (mid-segment)" value="✓ ON" />
        <FormRow
          label="Premoderation rate stop-trigger"
          value="20% → СТОП"
        />
      </Section>

      <Section title="Токены интеграций">
        <IntegrationSecretsPanel />
      </Section>

      <Section title="OAuth-токены">
        <FormRow
          label="Я.Директ"
          value="активен"
          hint="истекает через ~358 дней"
        />
        <FormRow
          label="Я.Метрика"
          value="активен"
          hint="counter 105615284 · истекает через ~358 дней"
        />
        <FormRow
          label="Bitrix24"
          value="не подключён"
          hint="опционально, для Phase 4"
        />
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--bg-elevated)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "1.25rem",
        marginBottom: "1rem",
      }}
    >
      <h3
        style={{
          margin: "0 0 0.75rem",
          fontSize: "0.85rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--text-dim)",
        }}
      >
        {title}
      </h3>
      <div>{children}</div>
    </div>
  );
}

function KillSwitchPanel() {
  const [active, setActive] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    api<{ key: string; value: boolean }>("/api/settings/app/kill_switch_active")
      .then((d) => setActive(!!d.value))
      .catch((e) => setErr(e instanceof Error ? e.message : "Ошибка"));
  }
  useEffect(load, []);

  async function toggle(next: boolean) {
    if (next && !confirm("ВКЛЮЧИТЬ kill-switch? Это заблокирует ВСЕ исходящие команды в Я.Директ — pause-all, resume, создание новых гипотез не сработают. Используется только в аварии.")) {
      return;
    }
    if (!next && !confirm("Снять kill-switch? Система снова сможет отправлять команды в Я.Директ.")) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api("/api/settings/app/kill_switch_active", {
        method: "PUT",
        body: JSON.stringify({ value: next }),
      });
      setActive(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setBusy(false);
    }
  }

  if (active === null) return <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>;

  return (
    <div style={{ padding: "0.4rem 0" }}>
      <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", marginBottom: "0.7rem", lineHeight: 1.5 }}>
        Блокирует <b>любые</b> write-операции в Я.Директ через write-client (создание / suspend / resume / изменение бюджета / минусация). Шире чем «Pause all» в гипотезах — здесь блокируются вообще все попытки робота отправить команду. Существующие 3 ЕПК в Я.Директе продолжают работать как раньше — мы их не трогаем; блокируется только наш агент.
      </div>
      <button
        onClick={() => toggle(!active)}
        disabled={busy}
        style={{
          width: "100%",
          padding: "1.2rem",
          background: active ? "var(--red)" : "rgba(248,113,113,0.05)",
          color: active ? "var(--bg)" : "var(--red)",
          border: "2px solid var(--red)",
          borderRadius: 10,
          cursor: busy ? "wait" : "pointer",
          fontSize: "1.05rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {active ? "🛑 KILL-SWITCH ON — снять" : "🚨 АВАРИЙНЫЙ СТОП"}
      </button>
      {active && (
        <div
          style={{
            marginTop: "0.7rem",
            padding: "0.6rem 0.9rem",
            background: "rgba(248,113,113,0.08)",
            border: "1px solid var(--red)",
            borderRadius: 6,
            color: "var(--red)",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          ⚠ Все исходящие команды агента в Я.Директ заблокированы. Существующие 3 ЕПК работают.
        </div>
      )}
      {err && <div style={{ color: "var(--red)", marginTop: "0.5rem", fontSize: "0.78rem" }}>{err}</div>}
    </div>
  );
}

function AutoKillToggle() {
  const [value, setValue] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<{ key: string; value: boolean }>("/api/settings/app/hypothesis_auto_kill")
      .then((d) => setValue(d.value))
      .catch((e) => setErr(e instanceof Error ? e.message : "Ошибка"));
  }, []);

  async function toggle() {
    if (value === null) return;
    setSaving(true);
    setErr(null);
    const next = !value;
    try {
      await api("/api/settings/app/hypothesis_auto_kill", {
        method: "PUT",
        body: JSON.stringify({ value: next }),
      });
      setValue(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        gap: "1rem",
        padding: "0.65rem 0",
        borderBottom: "1px solid var(--border)",
        alignItems: "center",
      }}
    >
      <div>
        <div style={{ fontWeight: 500 }}>Auto-kill при verdict=fail</div>
        <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "0.2rem" }}>
          Cron 08:00 МСК автоматически suspend-ит кампанию + переводит в killed, если ROAS &lt; target × 0.8
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
        <button
          onClick={toggle}
          disabled={saving || value === null}
          style={{
            padding: "0.4rem 0.9rem",
            background: value ? "var(--red)" : "var(--bg-elevated)",
            color: value ? "var(--bg)" : "var(--text-dim)",
            border: `1px solid ${value ? "var(--red)" : "var(--border)"}`,
            borderRadius: 16,
            cursor: saving ? "wait" : "pointer",
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          {value === null ? "..." : value ? "ON" : "OFF"}
        </button>
        {err && <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{err}</span>}
      </div>
    </div>
  );
}

function IntegrationSecretsPanel() {
  const [items, setItems] = useState<SecretStatus[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingName, setSavingName] = useState<string | null>(null);
  const [okName, setOkName] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    fetchSecretStatuses()
      .then(setItems)
      .catch((e) => setErr(e instanceof Error ? e.message : "Ошибка"));
  }
  useEffect(load, []);

  async function save(name: string) {
    const value = (drafts[name] ?? "").trim();
    if (!value) return;
    setSavingName(name);
    setErr(null);
    setOkName(null);
    try {
      await saveSecret(name, value);
      setDrafts((d) => ({ ...d, [name]: "" }));
      setOkName(name);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setSavingName(null);
    }
  }

  if (!items) return <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>;

  const badge = (s: SecretStatus["source"]) =>
    s === "db"
      ? { text: "задан через UI", color: "#4ade80" }
      : s === "env"
        ? { text: "в .env", color: "var(--text-dim)" }
        : { text: "не задан", color: "#f87171" };

  return (
    <div>
      <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", marginBottom: "0.7rem", lineHeight: 1.5 }}>
        Вставь токен и нажми «Сохранить» — он шифруется и кладётся в БД, файлы трогать не нужно.
        Текущее значение не показывается; пустой ввод игнорируется.
      </div>
      {items.map((it) => {
        const b = badge(it.source);
        const draft = drafts[it.name] ?? "";
        return (
          <div
            key={it.name}
            style={{
              display: "grid",
              gridTemplateColumns: "260px 1fr auto",
              gap: "0.75rem",
              padding: "0.6rem 0",
              borderBottom: "1px solid var(--border)",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>{it.label}</div>
              <div style={{ fontSize: "0.72rem", color: b.color, marginTop: "0.2rem" }}>
                {b.text}
                {okName === it.name ? " · сохранено" : ""}
              </div>
            </div>
            <input
              type="password"
              autoComplete="off"
              placeholder="вставить новый токен…"
              value={draft}
              onChange={(e) => setDrafts((d) => ({ ...d, [it.name]: e.target.value }))}
              style={{
                padding: "0.45rem 0.7rem",
                background: "var(--bg-elevated)",
                color: "inherit",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: "0.85rem",
              }}
            />
            <button
              onClick={() => save(it.name)}
              disabled={savingName === it.name || !draft.trim()}
              style={{
                padding: "0.45rem 1rem",
                background: "#93c5fd",
                color: "#0F1419",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                cursor: savingName === it.name || !draft.trim() ? "not-allowed" : "pointer",
                fontSize: "0.82rem",
              }}
            >
              {savingName === it.name ? "…" : "Сохранить"}
            </button>
          </div>
        );
      })}
      {err && <div style={{ color: "#f87171", marginTop: "0.5rem", fontSize: "0.78rem" }}>{err}</div>}
    </div>
  );
}

function FormRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "280px 1fr",
        gap: "1rem",
        padding: "0.65rem 0",
        borderBottom: "1px solid var(--border)",
        alignItems: "center",
      }}
    >
      <div>
        <div style={{ fontWeight: 500 }}>{label}</div>
        {hint && (
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-dim)",
              marginTop: "0.2rem",
            }}
          >
            {hint}
          </div>
        )}
      </div>
      <div className="mono">{value}</div>
    </div>
  );
}
